import * as d3 from "d3";

export default function ForceGraph({
  baseNodes, // an iterable of node objects (typically [{id}, …])
  baseLinks // an iterable of link objects (typically [{source, target}, …])
}, {
  onCourseClick,
  nodeRadius = 5, // node radius, in pixels
  linkStroke = "#999", // link stroke color
  linkStrokeOpacity = 0.6, // link stroke opacity
  linkStrokeWidth = 1, // given d in links, returns a stroke width in pixels
  linkStrokeLinecap = "round", // link stroke linecap
  linkStrength,
  colors = d3.schemeTableau10, // an array of color strings, for the node groups
  width = window.innerWidth * 0.9, // outer width, in pixels
  height = 1100, // outer height, in pixels
  invalidation, // when this promise resolves, stop the simulation
  selectedId = undefined
} = {}) {

  const nodes = [...baseNodes];
  let links = [...baseLinks];
  let selectedNode = selectedId ? baseNodes.find(node => node.id === selectedId) : undefined;

  const svg = d3.create("svg")
    .attr("viewBox", [-width * 0.8 / 2, -height * 0.8 / 2, width * 0.8, height * 0.8])
    .attr('width', width)
    .attr('height', height)

  let linkElements, nodeElements, textElements;

  const linkGroup = svg.append("g").attr('class', 'links')
  const nodeGroup = svg.append("g").attr('class', 'nodes')
  const textGroup = svg.append("g").attr('class', 'texts');

  const forceNode = d3.forceManyBody().strength(-150);
  const forceLink = d3.forceLink(links).id((node) => node.id)
  const simulation = d3.forceSimulation(nodes)
    .force("link", forceLink)
    .force("charge", forceNode)
    .force("collision", d3.forceCollide(n => getRadius(n) * 1.2))
    // .force("x", d3.forceX())
    // .force("y", d3.forceY())
    .force('center', d3.forceCenter(0, 0))

  const dragDrop = d3.drag()
    .on('start', (event, node) => {
      node.fx = node.x
      node.fy = node.y
    }).on('drag', (event, node) => {
      simulation.alphaTarget(0.7).restart()
      node.fx = event.x
      node.fy = event.y
    }).on('end', (event, node) => {
      if (!event.active) {
        simulation.alphaTarget(0)
      }
      node.fx = null
      node.fy = null
    })

  function getNeighbors(node) {
    return baseLinks.reduce((neighbors, link) => {
      if (link.source.id === node.id) {
        neighbors.push(baseNodes.find((item) => item.id === link.target.id))
      }
      else if (link.target.id === node.id) {
        neighbors.push(baseNodes.find(item => item.id === link.source.id))
      }
      return neighbors;
    }, [node])
  }

  function selectNode(node) {
    // reset the data if we click on the selected node (usefull for mobile navigation)
    if (selectedNode && node === selectedNode) {
      resetView();
    }
    else {
      onCourseClick(node);
      selectedNode = node
      updateData(node);
      updateSimulation();
    }
  }

  function resetView() {
    selectedNode = undefined;
    resetData();
    updateSimulation();
  }

  function updateData(selectedNode) {
    const neighbors = getNeighbors(selectedNode);
    const newNodes = baseNodes.filter((node) => {
      return neighbors.find(item => item.id === node.id) || node.id === selectedNode.id
    });
    const diff = {
      removed: nodes.filter(node => newNodes.indexOf(node) === -1),
      added: newNodes.filter(node => nodes.indexOf(node) === -1)
    }
    diff.removed.forEach(node => nodes.splice(nodes.indexOf(node), 1));
    diff.added.forEach(node => nodes.push(node));

    links = baseLinks.filter(link => {
      return link.target.id === selectedNode.id || link.source.id === selectedNode.id
    })
  }

  function updateGraph() {
    linkElements = linkGroup.selectAll('line').data(links, link => `${link.source.id}_${link.target.id}`)
    linkElements.exit().remove();

    const linkEnter = linkElements.enter()
      .append('line')
      .attr("stroke", linkStroke)
      .attr("stroke-opacity", linkStrokeOpacity)
      .attr("stroke-width", typeof linkStrokeWidth !== "function" ? linkStrokeWidth : null)
      .attr("stroke-linecap", linkStrokeLinecap);

    linkElements = linkEnter.merge(linkElements);

    nodeElements = nodeGroup.selectAll('circle').data(nodes, node => node.id);
    nodeElements.exit().remove();

    const nodeEnter = nodeElements.enter()
      .append('circle')
      .attr("stroke", '#fff')
      .attr("stroke-opacity", 1)
      .attr("stroke-width", 1)
      .style("cursor", "pointer")
      .attr("id", n => n.id)
      .attr("fill", (n) => colors[n.level])
      .on('click', (e, n) => {
        console.log({ n, selectedNode })
        if (n) selectNode(n);
      })
      .on('contextmenu', (e, n) => {
        e.preventDefault();
        resetView();
      })
      .call(dragDrop);

    nodeElements = nodeEnter.merge(nodeElements)

    nodeElements.attr("r", (n) => getRadius(n))

    svg.selectAll('title').exit().remove();
    nodeElements.append('title').text(node =>
      `${node.Type_de_diplome ? `${node.Type_de_diplome}\n` : ''}${node.name || ''}${node.Parcours_Specialites_ED ? `\n${node.Parcours_Specialites_ED}` : ''}`
    );


    textElements = textGroup.selectAll('foreignObject').data([]).exit().remove()
    textElements = textGroup.selectAll('foreignObject').data(nodes, node => node.id)

    const textEnter = textElements.enter()
      .append('foreignObject')
      .attr('style', 'pointer-events: none; ');

    textEnter
      .append('xhtml:div')
      .attr('class', node => `text-white text-xs w-full h-full flex ${node.level === 3 ? '' : 'items-center '}justify-center`)
      .attr('style', node => `pointer-events: none; font-size: ${getFontSize(node)}px; line-height: ${getFontSize(node) + 2}px;`)
      .html(node => getText(node))


    textElements = textEnter.merge(textElements);




  }

  function resetData() {
    baseNodes.forEach((node) => {
      if (!nodes.find(item => item.id === node.id)) {
        nodes.push(node);
      }
    });
    links = baseLinks;
  }


  function updateSimulation() {
    updateGraph();
    simulation.nodes(nodes).on('tick', () => {
      nodeElements.attr('cx', n => n.x).attr('cy', n => n.y);
      textElements
        .attr('x', n => n.x - 5 / 6 * getRadius(n))
        .attr('y', n => n.y - 4 / 6 * getRadius(n))
        .attr('width', n => 10 / 6 * getRadius(n))
        .attr('height', n => 8 / 6 * getRadius(n))
      linkElements
        .attr('x1', l => l.source.x)
        .attr('y1', l => l.source.y)
        .attr('x2', l => l.target.x)
        .attr('y2', l => l.target.y)
    });
    if (!!selectedNode) {
      forceNode.strength(-1000)
      // simulation.force('center', d3.forceCenter(width / 30, height / 30))
    }
    else {
      forceNode.strength(-50)
      // simulation.force("x", d3.forceX())
      //   .force("y", d3.forceY(0.5))
    }
    simulation.force('link').links(links)
    simulation.force("charge", forceNode);
    simulation.restart();
  }

  function getRadius(node) {
    if (!selectedNode) {
      return nodeRadius[node.level];
    }
    else {
      return selectedNode === node ? 50
        : selectedNode.level === node.level ? 36
          : selectedNode.level < node.level ? 42
            : 30
    }
  }

  function getFontSize(node) {
    if (node.level === 1) {
      return 10;
    }
    if (!!selectedNode && node.level === 2) {
      return 10;
    }
    return 8;
  }
  //chimie - pharma
  function getText(node) {
    const maxLength = getRadius(node) * 1.5;
    if (node.level < 3) {
      const text =
        node.name.length
          > (maxLength) ? node.name.slice(0, maxLength - 3) + '...' :
          node.name;
      return `<p class="text-center break-words" style="word-break: break-word; text-overflow: ellipsis;">${text.replace(' - ', ' -<br/>')}</p>`
    }
    if (selectedNode) {
      let diplome = '';
      let name = '';
      let specialty = '';
      if (node.Type_de_diplome.length > maxLength) {
        diplome = node.Type_de_diplome.slice(0, maxLength - 3) + '...';
      }
      else {
        diplome = node.Type_de_diplome;
        if ((diplome + node.name).length > maxLength) {
          name = node.name.slice(0, maxLength - diplome.length - 3) + '...'
        }
        else {
          name = node.name;
          if ((diplome + name + node.Parcours_Specialites_ED).length > maxLength) {
            specialty = node.Parcours_Specialites_ED.slice(0, maxLength - (diplome + name).length - 3) + '...'
          }
          else specialty = node.Parcours_Specialites_ED;
        }
      }
      return `<p class="text-center text-overflow: ellipsis;">${diplome ? `<span class="underline">${diplome}</span><br />` : ''}
      ${name || ''}<br/>
      ${specialty ? `<span class="font-t-italic">${specialty}</span>` : ''}
      </p>`
    }
    return '';
  }

  if (selectedNode) updateData(selectedNode);
  updateSimulation();

  return Object.assign(svg.node());
}